import { FC, useState, useEffect } from 'react'
import { useSessionHistory } from '../hooks/useSessionHistory'
import { SellerServiceSessionItem } from '../types'

interface HistoryTabProps {
  pluginspaceId: number
}

export const HistoryTab: FC<HistoryTabProps> = ({ pluginspaceId }) => {
  const { sessions, loading, summary, loadHistory } = useSessionHistory(pluginspaceId)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    setStartDate(yesterday)
    setEndDate(today)
    loadHistory(yesterday, today)
  }, [])

  const handleFilterApply = async () => {
    if (startDate && endDate) {
      await loadHistory(startDate, endDate)
    }
  }

  const getOutcomeColor = (outcome: string | null) => {
    switch (outcome) {
      case 'sale_completed':
        return 'success'
      case 'pending_checkout':
        return 'warning'
      case 'no_sale':
        return 'danger'
      case 'abandoned':
        return 'secondary'
      default:
        return 'light'
    }
  }

  const getOutcomeLabel = (outcome: string | null) => {
    switch (outcome) {
      case 'sale_completed':
        return 'Venda Concluída'
      case 'pending_checkout':
        return 'Esperando Checkout'
      case 'no_sale':
        return 'Sem Venda'
      case 'abandoned':
        return 'Abandonado'
      default:
        return '-'
    }
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}m ${secs}s`
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '300px' }}>
        <div className='spinner-border' role='status'>
          <span className='sr-only'>Carregando...</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      {summary && (
        <div className='row g-3 mb-5'>
          <div className='col-md-6 col-lg-3'>
            <div className='card bg-light-info'>
              <div className='card-body'>
                <div className='fs-4 fw-bold text-info'>{summary.totalSessions}</div>
                <div className='fs-7 text-gray-700'>Total de Sessões</div>
              </div>
            </div>
          </div>
          <div className='col-md-6 col-lg-3'>
            <div className='card bg-light-success'>
              <div className='card-body'>
                <div className='fs-4 fw-bold text-success'>{summary.totalSales}</div>
                <div className='fs-7 text-gray-700'>Vendas</div>
              </div>
            </div>
          </div>
          <div className='col-md-6 col-lg-3'>
            <div className='card bg-light-danger'>
              <div className='card-body'>
                <div className='fs-4 fw-bold text-danger'>{summary.totalNoSales}</div>
                <div className='fs-7 text-gray-700'>Sem Venda</div>
              </div>
            </div>
          </div>
          <div className='col-md-6 col-lg-3'>
            <div className='card bg-light-warning'>
              <div className='card-body'>
                <div className='fs-4 fw-bold text-warning'>
                  {Math.round((1000 * summary.avgDuration) / 1000 / 60)}m
                </div>
                <div className='fs-7 text-gray-700'>Duração Média</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='card mb-5'>
        <div className='card-header'>
          <h3 className='card-title'>Filtrar por Data</h3>
        </div>
        <div className='card-body'>
          <div className='row g-3'>
            <div className='col-md-4'>
              <label className='form-label'>Data Início</label>
              <input
                type='date'
                className='form-control'
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className='col-md-4'>
              <label className='form-label'>Data Fim</label>
              <input
                type='date'
                className='form-control'
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className='col-md-4 d-flex align-items-end'>
              <button className='btn btn-primary w-100' onClick={handleFilterApply} disabled={loading}>
                <i className='fas fa-search'></i> Filtrar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className='card'>
        <div className='card-header'>
          <h3 className='card-title'>Histórico de Atendimentos</h3>
        </div>
        <div className='card-body'>
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 gy-7'>
              <thead>
                <tr className='fw-bold fs-6 text-gray-800'>
                  <th>Vendedor</th>
                  <th>Posição</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Duração</th>
                  <th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='text-center py-5'>
                      Nenhum atendimento encontrado
                    </td>
                  </tr>
                ) : (
                  sessions.map((session) => (
                    <tr key={session.id}>
                      <td>
                        <span className='fw-bold'>{session.sellerName || 'Vendedor ' + session.sellerId}</span>
                      </td>
                      <td className='text-center'>{session.queuePositionAtStart}</td>
                      <td>
                        <small>
                          {new Date(session.startedAt).toLocaleString('pt-BR')}
                        </small>
                      </td>
                      <td>
                        <small>
                          {session.finishedAt ? new Date(session.finishedAt).toLocaleTimeString('pt-BR') : '-'}
                        </small>
                      </td>
                      <td>{formatDuration(session.durationSeconds)}</td>
                      <td>
                        <span className={`badge badge-light-${getOutcomeColor(session.outcome)}`}>
                          {getOutcomeLabel(session.outcome)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
